// src/features/task/create/draft.ts
// Draft preservation for the task creation form.
//
// Typing a task description and then mis-clicking a link should not cost the
// work. Everything the form holds — except File objects, which cannot be
// serialised — is mirrored into sessionStorage, keyed by the scope the task is
// being created in so two tabs creating in two projects never collide.

import type { Tag } from '@core/types/tag';
import type { CustomFieldDraftValue } from './logic';

// v2 stores the parent *kind* beside the parent type, because "no parent" and
// "the whole tenant" are two different answers that a type name alone cannot
// tell apart. Older drafts are dropped rather than guessed at.
export const TASK_CREATE_DRAFT_VERSION = 2;

export interface ChecklistItemDraft {
  id: string;
  content: string;
  is_done: boolean;
}

export interface ChecklistDraft {
  title: string;
  items: ChecklistItemDraft[];
}

export interface WatcherDraft {
  id: string;
  name: string;
}

export interface TaskCreateDraft {
  version: number;
  savedAt: number;
  title: string;
  description: string;
  typeId: string;
  categoryId: string;
  priority: string;
  assignee: string;
  assigneeName: string;
  dueDate: string;
  startDate: string;
  estimatedHours: string;
  parentId: string;
  /** One of PARENT_KINDS — 'none' included, and it is the default. */
  parentKind: string;
  /** The entity type name sent to the backend; empty when there is no parent. */
  parentType: string;
  templateId: string | null;
  tags: Tag[];
  customFieldValues: Record<string, CustomFieldDraftValue>;
  customFieldLabels: Record<string, string>;
  checklists: ChecklistDraft[];
  watchers: WatcherDraft[];
}

/** Storage key — one draft per (product, parent) pair. */
export function draftKey(productId: string, parentId: string): string {
  return `corde.taskCreate.v${TASK_CREATE_DRAFT_VERSION}.${productId || 'default'}.${parentId || 'root'}`;
}

/** True when the draft holds anything a person would be sad to lose. */
export function draftHasContent(draft: TaskCreateDraft | null): boolean {
  if (!draft) return false;
  return Boolean(
    draft.title.trim() ||
      draft.description.trim() ||
      draft.tags.length ||
      draft.checklists.some((cl) => cl.items.some((it) => it.content.trim())) ||
      draft.watchers.length ||
      Object.values(draft.customFieldValues).some((v) =>
        Array.isArray(v) ? v.length > 0 : String(v ?? '').trim() !== '',
      ),
  );
}

export function loadDraft(key: string): TaskCreateDraft | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TaskCreateDraft;
    if (parsed?.version !== TASK_CREATE_DRAFT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveDraft(key: string, draft: Omit<TaskCreateDraft, 'version' | 'savedAt'>): void {
  try {
    const payload: TaskCreateDraft = {
      ...draft,
      version: TASK_CREATE_DRAFT_VERSION,
      savedAt: Date.now(),
    };
    sessionStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // Storage full or blocked — draft safety is best-effort, never fatal.
  }
}

export function clearDraft(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}
